/**
 * AffiliateManagerPage — assign internal managers (primary + backup) to
 * publishers / affiliates. Query by manager (workload view) or by affiliate
 * (ownership view). Create/reassign + remove.
 *
 * Brand green (emerald) accent on white/zinc chrome, 6-8px rounding.
 */
import { useState, type FormEvent } from "react";
import {
	useAffiliateManagerCreate,
	useAffiliateManagerDelete,
	useAffiliatesByManager,
	useAssignmentsByAffiliate,
} from "./hooks";
import type { AffiliateManagerRole, AffiliateManagerRow } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const TAB =
	"rounded-[6px] border px-3 py-1.5 text-xs font-medium transition-colors";
const TAB_ACTIVE = `${TAB} border-green-600 bg-green-50 text-green-700`;
const TAB_IDLE = `${TAB} border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50`;

const ROLE_BADGE: Record<AffiliateManagerRole, string> = {
	primary: "bg-green-50 text-green-700 ring-green-600/20",
	backup: "bg-zinc-100 text-zinc-700 ring-zinc-500/20",
};

function RoleBadge({ role }: { role: AffiliateManagerRole }) {
	return (
		<span
			className={`inline-flex items-center rounded-[6px] px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${ROLE_BADGE[role]}`}
			data-testid={`affiliate-manager-role-${role}`}
		>
			{role.charAt(0).toUpperCase() + role.slice(1)}
		</span>
	);
}

type Mode = "by-manager" | "by-affiliate";

export function AffiliateManagerPage() {
	const [mode, setMode] = useState<Mode>("by-manager");
	const [lookupId, setLookupId] = useState("");
	const [queryId, setQueryId] = useState<string | null>(null);

	const managerQ = useAffiliatesByManager(
		mode === "by-manager" ? queryId : null,
	);
	const affiliateQ = useAssignmentsByAffiliate(
		mode === "by-affiliate" ? queryId : null,
	);

	const activeQ = mode === "by-manager" ? managerQ : affiliateQ;

	const createMut = useAffiliateManagerCreate();
	const deleteMut = useAffiliateManagerDelete();

	const [assignmentId, setAssignmentId] = useState("");
	const [affiliateId, setAffiliateId] = useState("");
	const [managerId, setManagerId] = useState("");
	const [role, setRole] = useState<AffiliateManagerRole>("primary");

	const handleLookup = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = lookupId.trim();
		if (!trimmed) return;
		setQueryId(trimmed);
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!assignmentId.trim() || !affiliateId.trim() || !managerId.trim()) {
			return;
		}
		createMut.mutate(
			{
				id: assignmentId.trim(),
				affiliate_id: affiliateId.trim(),
				manager_id: managerId.trim(),
				role,
			},
			{
				onSuccess: () => {
					setAssignmentId("");
					setAffiliateId("");
					setManagerId("");
					setRole("primary");
				},
			},
		);
	};

	const rows: AffiliateManagerRow[] =
		mode === "by-manager"
			? (managerQ.data?.assignments ?? [])
			: (affiliateQ.data?.assignments ?? []);

	const workload =
		mode === "by-manager"
			? (managerQ.data?.workload ?? 0)
			: (affiliateQ.data?.count ?? 0);

	const lookupLabel =
		mode === "by-manager" ? "Manager ID" : "Affiliate ID";
	const lookupPlaceholder =
		mode === "by-manager"
			? "Enter manager ID (e.g. mgr_42)"
			: "Enter affiliate ID (e.g. aff_123)";

	return (
		<main
			data-testid="affiliate-manager-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					affiliate-manager
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Affiliate managers
				</h1>
				<p className="text-sm text-zinc-600">
					Assign one primary and one optional backup manager per
					publisher. Reassigning the same role overwrites the
					existing row.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Assign manager"
				data-testid="affiliate-manager-assign"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Assign manager
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4"
					data-testid="affiliate-manager-create-form"
				>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Assignment ID
						<input
							type="text"
							className={INPUT}
							placeholder="asg_1"
							value={assignmentId}
							onChange={(e) => setAssignmentId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-manager-id-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Affiliate ID
						<input
							type="text"
							className={INPUT}
							placeholder="aff_123"
							value={affiliateId}
							onChange={(e) => setAffiliateId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-manager-affiliate-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Manager ID
						<input
							type="text"
							className={INPUT}
							placeholder="mgr_42"
							value={managerId}
							onChange={(e) => setManagerId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-manager-manager-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Role
						<select
							className={SELECT}
							value={role}
							onChange={(e) =>
								setRole(
									e.currentTarget.value as AffiliateManagerRole,
								)
							}
							disabled={createMut.isPending}
							data-testid="affiliate-manager-role-input"
						>
							<option value="primary">Primary</option>
							<option value="backup">Backup</option>
						</select>
					</label>
					<div className="sm:col-span-4">
						<button
							type="submit"
							className={BTN}
							disabled={
								createMut.isPending ||
								!assignmentId.trim() ||
								!affiliateId.trim() ||
								!managerId.trim()
							}
							data-testid="affiliate-manager-submit"
						>
							{createMut.isPending ? "Saving" : "Save assignment"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="affiliate-manager-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
				{createMut.data ? (
					<p
						className="mt-2 text-xs text-green-700"
						data-testid="affiliate-manager-create-ok"
					>
						{createMut.data.reassigned ? "Reassigned" : "Assigned"}{" "}
						{createMut.data.assignment.manager_id} as{" "}
						{createMut.data.assignment.role} for affiliate{" "}
						{createMut.data.assignment.affiliate_id}.
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Lookup assignments">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Lookup assignments
					</h2>
					<div
						className="flex items-center gap-2"
						role="tablist"
						aria-label="Lookup mode"
					>
						<button
							type="button"
							className={mode === "by-manager" ? TAB_ACTIVE : TAB_IDLE}
							onClick={() => {
								setMode("by-manager");
								setQueryId(null);
							}}
							data-testid="affiliate-manager-tab-manager"
							role="tab"
							aria-selected={mode === "by-manager"}
						>
							By manager
						</button>
						<button
							type="button"
							className={mode === "by-affiliate" ? TAB_ACTIVE : TAB_IDLE}
							onClick={() => {
								setMode("by-affiliate");
								setQueryId(null);
							}}
							data-testid="affiliate-manager-tab-affiliate"
							role="tab"
							aria-selected={mode === "by-affiliate"}
						>
							By affiliate
						</button>
					</div>
				</div>

				<form
					onSubmit={handleLookup}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="affiliate-manager-lookup-form"
				>
					<label className="sr-only" htmlFor="affiliate-manager-lookup">
						{lookupLabel}
					</label>
					<input
						id="affiliate-manager-lookup"
						type="text"
						className={INPUT}
						placeholder={lookupPlaceholder}
						value={lookupId}
						onChange={(e) => setLookupId(e.currentTarget.value)}
						data-testid="affiliate-manager-lookup-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={!lookupId.trim()}
						data-testid="affiliate-manager-lookup-submit"
					>
						Look up
					</button>
				</form>

				<div className="mt-4">
					{queryId === null ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="affiliate-manager-idle"
						>
							Enter a {lookupLabel.toLowerCase()} to list assignments.
						</p>
					) : activeQ.isLoading ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="affiliate-manager-loading"
						>
							Loading
						</p>
					) : activeQ.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="affiliate-manager-error"
						>
							{activeQ.error.message}
						</p>
					) : rows.length === 0 ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="affiliate-manager-empty"
						>
							No assignments found.
						</p>
					) : (
						<>
							<p
								className="text-xs text-zinc-500"
								data-testid="affiliate-manager-count"
							>
								{mode === "by-manager" ? "Workload" : "Assignments"}:{" "}
								{workload}
							</p>
							<div className="mt-2 overflow-hidden rounded-[8px] border border-zinc-200">
								<table
									className="w-full divide-y divide-zinc-200 text-sm"
									data-testid="affiliate-manager-table"
								>
									<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
										<tr>
											<th className="px-3 py-2 text-left font-medium">
												Assignment
											</th>
											<th className="px-3 py-2 text-left font-medium">
												Affiliate
											</th>
											<th className="px-3 py-2 text-left font-medium">
												Manager
											</th>
											<th className="px-3 py-2 text-left font-medium">
												Role
											</th>
											<th className="px-3 py-2 text-left font-medium">
												Assigned
											</th>
											<th className="px-3 py-2 text-right font-medium">
												Actions
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-zinc-100 bg-white">
										{rows.map((row) => (
											<tr
												key={row.id}
												data-testid={`affiliate-manager-row-${row.id}`}
											>
												<td className="px-3 py-2 font-mono text-xs text-zinc-700">
													{row.id}
												</td>
												<td className="px-3 py-2 font-mono text-xs text-zinc-700">
													{row.affiliate_id}
												</td>
												<td className="px-3 py-2 font-mono text-xs text-zinc-700">
													{row.manager_id}
												</td>
												<td className="px-3 py-2">
													<RoleBadge role={row.role} />
												</td>
												<td className="px-3 py-2 text-xs text-zinc-500">
													{new Date(row.assigned_at * 1000)
														.toISOString()
														.slice(0, 10)}
												</td>
												<td className="px-3 py-2 text-right">
													<button
														type="button"
														className={BTN_GHOST}
														onClick={() =>
															deleteMut.mutate(row.id)
														}
														disabled={deleteMut.isPending}
														data-testid={`affiliate-manager-delete-${row.id}`}
													>
														Unassign
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							{deleteMut.error ? (
								<p
									className="mt-2 text-xs text-red-600"
									role="alert"
									data-testid="affiliate-manager-delete-error"
								>
									{deleteMut.error.message}
								</p>
							) : null}
						</>
					)}
				</div>
			</section>
		</main>
	);
}

export default AffiliateManagerPage;
